import { lazy, ComponentType } from "react";

const RELOAD_KEY = "chunk-reload-at";

/**
 * Lazy import that survives stale build chunks.
 * After a new deploy, old chunk URLs 404 ("Failed to fetch dynamically imported module").
 * We retry once, then force a single hard reload to pick up the new build.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      // second attempt with cache-busting network retry
      try {
        await new Promise((r) => setTimeout(r, 400));
        return await factory();
      } catch (retryError) {
        const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
        const now = Date.now();
        if (now - last > 10000) {
          sessionStorage.setItem(RELOAD_KEY, String(now));
          window.location.reload();
          // keep suspense pending while reloading
          return await new Promise<{ default: T }>(() => {});
        }
        throw retryError;
      }
    }
  });
}
